import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'

interface LogGroup {
  date: string
  logs: AuditLogs[]
}

export function normalizeAuditStatus(status?: string): string | undefined {
  return status?.toLowerCase()
}

export function getStatusColor(status?: string): string {
  switch (normalizeAuditStatus(status)) {
    case 'success':
      return 'green.500'
    case 'error':
      return 'red.500'
    case 'denied':
      return 'orange.500'
    default:
      return 'gray.400'
  }
}

export function getStatusColorPalette(status?: string): string {
  switch (normalizeAuditStatus(status)) {
    case 'success':
      return 'green'
    case 'error':
      return 'red'
    case 'denied':
      return 'orange'
    default:
      return 'gray'
  }
}

/**
 * Formats a timestamp as time only (HH:MM:SS)
 */
export function formatTime(timestamp?: number): string {
  if (!timestamp) {
    return '-'
  }
  try {
    const date = new Date(timestamp)
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return String(timestamp)
  }
}

/**
 * Formats a timestamp as full date and time
 */
export function formatDateTime(timestamp?: number): string {
  if (!timestamp) {
    return '-'
  }
  try {
    const date = new Date(timestamp)
    return date.toLocaleString()
  } catch {
    return String(timestamp)
  }
}

/**
 * Formats JSON data for display
 */
export function formatJson(data: unknown): string {
  if (!data) {
    return '-'
  }
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return '[Complex Object]'
  }
}

/**
 * Helper to get date string for grouping logs
 */
export function getDateKey(timestamp?: number): string {
  if (!timestamp) {
    return 'Unknown'
  }
  const date = new Date(timestamp)
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Groups audit logs by date
 */
export function groupLogsByDate(logs: AuditLogs[]): LogGroup[] {
  const groups: Map<string, AuditLogs[]> = new Map()

  for (const log of logs) {
    const dateKey = getDateKey(log.timestamp)
    const existing = groups.get(dateKey) || []
    existing.push(log)
    groups.set(dateKey, existing)
  }

  return Array.from(groups.entries()).map(([date, groupLogs]) => ({
    date,
    logs: groupLogs,
  }))
}

// Priority keys to display in the resource summary
const PRIORITY_KEYS = ['hive_id', 'cluster_id', 'org_id', 'clusterName', 'type']

/**
 * The action an audit entry recorded, e.g. `organization:unlink_salesforce_account`.
 *
 * It is not a first-class field on `AuditLogs` — writers put it in
 * `request_metadata`, which the API types as an open map, so it arrives as
 * `any` and has to be narrowed here rather than trusted.
 *
 * Not every writer sets it (`ClustersDraftsController` does not), so an absent
 * action is ordinary and renders as a dash, the same as an absent user.
 *
 * Returned verbatim rather than prettified: it is the value an operator greps
 * for and matches against what the backend logged, so rewriting it into prose
 * would make the column readable and useless.
 */
export function formatAction(requestMetadata?: {
  [key: string]: unknown
}): string {
  const action = requestMetadata?.action
  return typeof action === 'string' && action.trim() !== '' ? action : '-'
}

/**
 * Formats a resource object into a displayable string
 */
export function formatResource(resource?: { [key: string]: string }): string {
  if (!resource) {
    return '-'
  }
  const entries = Object.entries(resource)
  if (entries.length === 0) {
    return '-'
  }

  // Find priority keys that exist in the resource
  const priorityEntries: [string, string][] = []
  const otherEntries: [string, string][] = []

  for (const [key, value] of entries) {
    if (PRIORITY_KEYS.includes(key)) {
      priorityEntries.push([key, value])
    } else {
      otherEntries.push([key, value])
    }
  }

  // Sort priority entries by their priority order
  priorityEntries.sort(
    (a, b) => PRIORITY_KEYS.indexOf(a[0]) - PRIORITY_KEYS.indexOf(b[0]),
  )

  // Combine priority entries first, then others
  const sortedEntries = [...priorityEntries, ...otherEntries]

  // Show up to 2 key-value pairs
  const displayEntries = sortedEntries.slice(0, 2)
  const remaining = sortedEntries.length - displayEntries.length

  const summary = displayEntries.map(([k, v]) => `${k}: ${v}`).join(' | ')
  if (remaining > 0) {
    return `${summary} (+${remaining})`
  }
  return summary
}
